import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { app } from './firebase';
import { Product, OrderDetails } from '../interface/interfaces';

// Initialize Firebase Analytics
const analytics = getAnalytics(app);

// Device and app information
interface DeviceInfo {
  device_platform: string;
  device_id?: string;
  device_model?: string;
  os_version?: string;
}

interface AppInfo {
  app_id: string;
  app_name: string;
  app_version: string;
  build_number: string;
}

class AnalyticsService {
  private static deviceData: DeviceInfo | null = null;
  private static appData: AppInfo | null = null;
  private static readonly currency = 'NGN'; // Default currency

  /**
   * Initialize device and app information
   */
  static async initializeDeviceInfo(): Promise<void> {
    try {
      this.deviceData = await this.getDeviceInfo();
      this.appData = await this.getAppInfo();
      await this.setDeviceUserProperties();
    } catch (error) {
      console.error('Error initializing device/app info →', error);
    }
  }

  /**
   * Get device information
   */
  private static async getDeviceInfo(): Promise<DeviceInfo> {
    const userAgent = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    
    return {
      device_platform: isMobile ? 'Mobile Web' : 'Desktop Web',
      device_model: navigator.platform,
      os_version: navigator.userAgent,
      device_id: this.generateDeviceId(),
    };
  }

  /**
   * Get app information
   */
  private static async getAppInfo(): Promise<AppInfo> {
    return {
      app_id: 'yayako-web',
      app_name: 'Yayako',
      app_version: '1.0.0',
      build_number: '1',
    };
  }

  /**
   * Generate a unique device ID
   */
  private static generateDeviceId(): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
      return canvas.toDataURL();
    }
    return `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Set device user properties
   */
  private static async setDeviceUserProperties(): Promise<void> {
    const data = { ...this.deviceData, ...this.appData };
    const userProperties: Record<string, string> = {};
    
    for (const [key, value] of Object.entries(data)) {
      if (value != null) {
        userProperties[key] = value.toString();
      }
    }
    
    if (Object.keys(userProperties).length > 0) {
      await setUserProperties(analytics, userProperties);
    }
  }

  /**
   * Sanitize parameters for analytics
   */
  private static sanitize(raw: Record<string, any>): Record<string, string | number> {
    return Object.fromEntries(
      Object.entries(raw).map(([k, v]) => [
        k,
        typeof v === 'string' || typeof v === 'number' ? v : String(v)
      ])
    );
  }

  /**
   * Get default parameters for all events
   */
  private static getDefaultParams(): Record<string, string | number> {
    return this.sanitize({
      ...this.deviceData,
      ...this.appData,
    });
  }

  /**
   * Convert product to analytics event item
   */
  private static toEventItem(item: Product | OrderDetails): Record<string, any> {
    let itemId: string;
    let itemName: string;
    
    if ('productId' in item && 'name' in item) {
      // This is a Product
      itemId = item.productId;
      itemName = item.name;
    } else if ('id' in item && 'productName' in item) {
      // This is an OrderDetails
      itemId = item.id;
      itemName = item.productName;
    } else {
      // Fallback
      itemId = 'unknown';
      itemName = 'unknown';
    }
    
    return {
      item_id: itemId || 'unknown',
      item_name: itemName || 'unknown',
      item_category: 'product',
      quantity: 'quantity' in item ? item.quantity : 1,
      price: 'price_data' in item ? item.price_data.current_price : 
             'totalAmount' in item ? item.totalAmount : 0,
    };
  }

  /**
   * Log custom event
   */
  static async logCustomEvent(
    eventName: string,
    parameters: Record<string, any> = {}
  ): Promise<void> {
    await logEvent(analytics, eventName as any, {
      ...this.getDefaultParams(),
      ...this.sanitize(parameters),
    });
  }

  /**
   * Log view item
   */
  static async logViewItem(
    item: Product,
    value: number,
    currency: string = this.currency
  ): Promise<void> {
    await logEvent(analytics, 'view_item' as any, {
      currency,
      value,
      items: [this.toEventItem(item)],
      ...this.getDefaultParams(),
    });
  }

  /**
   * Log add to cart
   */
  static async logAddToCart(
    item: Product | OrderDetails,
    value: number,
    currency: string = this.currency
  ): Promise<void> {
    await logEvent(analytics, 'add_to_cart' as any, {
      currency,
      value,
      items: [this.toEventItem(item)],
      ...this.getDefaultParams(),
    });
  }

  /**
   * Log remove from cart
   */
  static async logRemoveFromCart(
    item: Product | OrderDetails,
    value: number,
    currency: string = this.currency
  ): Promise<void> {
    await logEvent(analytics, 'remove_from_cart' as any, {
      currency,
      value,
      items: [this.toEventItem(item)],
      ...this.getDefaultParams(),
    });
  }

  /**
   * Log view cart
   */
  static async logViewCart(
    items: (Product | OrderDetails)[],
    currency: string = this.currency
  ): Promise<void> {
    await logEvent(analytics, 'view_cart' as any, {
      currency,
      items: items.map(item => this.toEventItem(item)),
      ...this.getDefaultParams(),
    });
  }

  /**
   * Log begin checkout
   */
  static async logBeginCheckout(
    value: number,
    items: (Product | OrderDetails)[],
    currency: string = this.currency
  ): Promise<void> {
    await logEvent(analytics, 'begin_checkout' as any, {
      value,
      currency,
      items: items.map(item => this.toEventItem(item)),
      ...this.getDefaultParams(),
    });
  }

  /**
   * Log purchase
   */
  static async logPurchase(
    transactionId: string,
    value: number,
    items: (Product | OrderDetails)[],
    currency: string = this.currency,
    affiliation?: string,
    tax?: number,
    shipping?: number,
    coupon?: string
  ): Promise<void> {
    const parameters: Record<string, any> = {
      currency,
      transaction_id: transactionId,
      value,
      items: items.map(item => this.toEventItem(item)),
      ...this.getDefaultParams(),
    };

    if (affiliation) parameters.affiliation = affiliation;
    if (tax) parameters.tax = tax;
    if (shipping) parameters.shipping = shipping;
    if (coupon) parameters.coupon = coupon;

    await logEvent(analytics, 'purchase' as any, parameters);
  }

  /**
   * Log add to wishlist
   */
  static async logAddToWishlist(
    item: Product,
    value: number,
    currency: string = this.currency
  ): Promise<void> {
    await logEvent(analytics, 'add_to_wishlist' as any, {
      currency,
      value,
      items: [this.toEventItem(item)],
      ...this.getDefaultParams(),
    });
  }

  /**
   * Set user ID
   */
  static async setUserId(userId: string | null): Promise<void> {
    if (userId) {
      await setUserId(analytics, userId);
    }
  }

  /**
   * Log page view with custom parameters
   */
  static async logPageView(
    pageName: string,
    additionalParams: Record<string, any> = {}
  ): Promise<void> {
    await this.logCustomEvent('page_view', {
      page_name: pageName,
      page_title: document.title,
      page_location: window.location.href,
      ...additionalParams,
    });
  }
}

export default AnalyticsService;
